<template>
  <div class="offers-page calendar-matrix-page">
    <div class="hero-card">
      <div>
        <h1>Kalendari i Çmimeve</h1>
        <p>Ndiqni ndryshimet e çmimeve për produktet gjatë muajit ose një periudhe të zgjedhur</p>
      </div>
      <div class="calendar-hero-controls">
        <div class="mode-switch period-mode-switch" style="margin-bottom: 0">
          <button
            type="button"
            class="mode-btn"
            :class="{ active: periodMode === 'month' }"
            @click="setPeriodMode('month')"
          >
            Muaji
          </button>
          <button
            type="button"
            class="mode-btn"
            :class="{ active: periodMode === 'custom' }"
            @click="setPeriodMode('custom')"
          >
            Periudhë
          </button>
        </div>

        <template v-if="periodMode === 'month'">
          <label class="calendar-field">
            <span>Muaji:</span>
            <select v-model.number="month" class="input-clean" @change="onPeriodChange">
              <option v-for="m in monthOptions" :key="m.value" :value="m.value">{{ m.label }}</option>
            </select>
          </label>
          <label class="calendar-field">
            <span>Viti:</span>
            <select v-model.number="year" class="input-clean" @change="onPeriodChange">
              <option v-for="y in yearOptions" :key="y" :value="y">{{ y }}</option>
            </select>
          </label>
        </template>
        <template v-else>
          <label class="calendar-field">
            <span>Nga:</span>
            <input v-model="dateFrom" class="input-clean" type="date" @change="onPeriodChange" />
          </label>
          <label class="calendar-field">
            <span>Deri:</span>
            <input v-model="dateTo" class="input-clean" type="date" @change="onPeriodChange" />
          </label>
          <div class="period-presets">
            <button type="button" class="button is-small is-light" @click="applyPreset('7')">7 ditë</button>
            <button type="button" class="button is-small is-light" @click="applyPreset('30')">30 ditë</button>
            <button type="button" class="button is-small is-light" @click="applyPreset('month')">Muaji aktual</button>
          </div>
        </template>

        <input
          v-model="filters.code_name"
          class="input-clean calendar-search"
          type="text"
          placeholder="Kërko produktin..."
          @input="scheduleReload"
        />
        <button type="button" class="button is-small is-primary" :disabled="loading" @click="loadMatrix">
          {{ loading ? "Duke ngarkuar..." : "Shfaq" }}
        </button>
        <button
          type="button"
          class="button is-small is-light"
          :disabled="loading || loadingMore"
          title="Rifresko te dhenat e kalendarit"
          @click="refreshMatrix"
        >
          {{ loading ? "..." : "Rifresko" }}
        </button>
        <button type="button" class="button is-small is-light" :disabled="exporting" @click="exportCsv">
          {{ exporting ? "Duke eksportuar..." : "Eksporto Excel" }}
        </button>
      </div>
    </div>

    <div class="panel-card calendar-filters">
      <div class="calendar-price-filters">
        <div class="mode-switch" style="margin-bottom: 0">
          <button type="button" class="mode-btn" :class="{ active: targetField === 'price' }" @click="setTargetField('price')">
            Cmimi i Shitjes
          </button>
          <button
            type="button"
            class="mode-btn"
            :class="{ active: targetField === 'default_price' }"
            @click="setTargetField('default_price')"
          >
            Cmim baze
          </button>
        </div>
        <div class="calendar-legend">
          <button
            type="button"
            class="legend-filter"
            :class="{ active: changeFilters.up }"
            title="Shfaq produktet me rritje cmimi ne kete periudhe (te gjitha, jo vetem faqja)"
            @click="toggleChangeFilter('up')"
          >
            <i class="legend-swatch up"></i> Rritje e çmimit
          </button>
          <button
            type="button"
            class="legend-filter"
            :class="{ active: changeFilters.down }"
            title="Shfaq produktet me ulje cmimi ne kete periudhe (te gjitha, jo vetem faqja)"
            @click="toggleChangeFilter('down')"
          >
            <i class="legend-swatch down"></i> Ulje e çmimit
          </button>
          <button
            type="button"
            class="legend-filter"
            :class="{ active: changeFilters.offer }"
            title="Shfaq produktet me ndryshim nga oferta ne kete periudhe (te gjitha, jo vetem faqja)"
            @click="toggleChangeFilter('offer')"
          >
            <i class="legend-swatch offer"></i> Ndryshim nga oferta
          </button>
          <span v-if="anyChangeFilterActive" class="legend-filter-hint">
            {{ total }} produkte me ndryshim
            <button type="button" class="button is-small is-light" @click="clearChangeFilters">Pastro</button>
          </span>
          <span v-if="loadingMore" class="legend-filter-hint">Duke ngarkuar me... {{ rows.length }}</span>
        </div>
      </div>

      <div class="quick-actions calendar-filter-row">
        <select v-model="filters.category_level_1" class="input-clean" @change="onCategoryL1Change">
          <option value="">Kategoria Prind (niveli 1)</option>
          <option v-for="o in categoryOptions.level1" :key="'l1-' + o.value" :value="o.value">{{ o.label }}</option>
        </select>
        <select v-model="filters.category_level_2" class="input-clean" @change="onCategoryL2Change">
          <option value="">Nënkategoria (niveli 2)</option>
          <option v-for="o in categoryOptions.level2" :key="'l2-' + o.value" :value="o.value">{{ o.label }}</option>
        </select>
        <select v-model="filters.category_level_3" class="input-clean" @change="scheduleReload">
          <option value="">Nënkategoria (niveli 3)</option>
          <option v-for="o in categoryOptions.level3" :key="'l3-' + o.value" :value="o.value">{{ o.label }}</option>
        </select>
        <select v-model="filters.tipi" class="input-clean" @change="scheduleReload">
          <option value="">Tipi</option>
          <option value="product">Produkt</option>
          <option value="accessory">Aksesor</option>
        </select>
        <input
          v-model="filters.description"
          class="input-clean"
          type="text"
          placeholder="Përshkrimi..."
          @input="scheduleReload"
        />
      </div>
    </div>

    <div v-if="errorMessage" class="panel-card">
      <p class="empty-note" style="color: #b91c1c">{{ errorMessage }}</p>
    </div>

    <div class="panel-card calendar-grid-card">
      <div v-if="loading && !rows.length" class="empty-note">Duke ngarkuar kalendarin...</div>
      <div v-else-if="!loading && !rows.length" class="empty-note">Nuk ka produkte per filtrat e zgjedhur.</div>
      <div v-else class="calendar-scroll" @mouseleave="clearHover">
        <table
          ref="matrixTable"
          class="calendar-matrix-table"
          @mouseover="onMatrixMouseOver"
        >
          <thead>
            <tr>
              <th class="sticky-col col-l1">Kategoria Prind</th>
              <th class="sticky-col col-l2">Nënkategoria (L2)</th>
              <th class="sticky-col col-l3">Nënkategoria (L3)</th>
              <th class="sticky-col col-tipi">Tipi</th>
              <th class="sticky-col col-code">Kodi</th>
              <th class="sticky-col col-name">Përshkrimi</th>
              <th
                v-for="d in days"
                :key="'dh-' + d.key"
                class="day-head"
                :class="{ weekend: d.is_weekend }"
                :data-col="d.key"
              >
                <div class="day-num">{{ d.d }}</div>
                <div class="day-wd">{{ periodMode === 'custom' ? (d.month_short || d.weekday) : d.weekday }}</div>
                <div class="day-arrow" aria-hidden="true">▲</div>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in rows" :key="row.id">
              <td class="sticky-col col-l1">{{ row.category_level_1 || "—" }}</td>
              <td class="sticky-col col-l2">{{ row.category_level_2 || "—" }}</td>
              <td class="sticky-col col-l3">{{ row.category_level_3 || "—" }}</td>
              <td class="sticky-col col-tipi">{{ row.tipi }}</td>
              <td class="sticky-col col-code">{{ row.code }}</td>
              <td class="sticky-col col-name" :title="row.name">{{ row.name }}</td>
              <td
                v-for="cell in row.days"
                :key="row.id + '-' + (cell.key || cell.d)"
                class="day-cell"
                :class="{
                  'delta-up': cell.delta === 'up',
                  'delta-down': cell.delta === 'down',
                  'is-offer': cell.is_offer,
                }"
                :data-col="cell.key || cell.d"
                @click="openCell(row, cell)"
              >
                <span class="day-cell-inner">
                  <span v-if="cell.delta === 'up'" class="delta-mark" aria-hidden="true">▲</span>
                  <span v-else-if="cell.delta === 'down'" class="delta-mark" aria-hidden="true">▼</span>
                  {{ formatCell(cell.value) }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="calendar-footer">
        <div class="calendar-pager" v-if="totalPages > 1">
          <button type="button" class="button is-small is-light" :disabled="page <= 1 || loading" @click="goPage(page - 1)">
            ←
          </button>
          <span>{{ page }} / {{ totalPages }} ({{ total }} produkte)</span>
          <button
            type="button"
            class="button is-small is-light"
            :disabled="page >= totalPages || loading"
            @click="goPage(page + 1)"
          >
            →
          </button>
        </div>
        <div v-else class="calendar-pager muted">{{ total }} produkte</div>
      </div>
    </div>

    <div v-if="drawer.open" class="calendar-drawer-backdrop" @click.self="closeDrawer">
      <aside class="calendar-drawer">
        <header class="calendar-drawer-head">
          <div>
            <h2>{{ drawer.product?.code }}</h2>
            <p>{{ drawer.product?.name }}</p>
            <p class="muted">{{ drawer.date }}</p>
          </div>
          <button type="button" class="delete" aria-label="close" @click="closeDrawer"></button>
        </header>
        <div v-if="drawer.loading" class="empty-note">Duke ngarkuar...</div>
        <div v-else-if="drawer.error" class="empty-note" style="color: #b91c1c">{{ drawer.error }}</div>
        <div v-else class="calendar-drawer-body">
          <div class="drawer-prices">
            <div>
              <div class="muted">Cmimi</div>
              <div class="price-val">{{ money(drawer.price) }}</div>
            </div>
            <div>
              <div class="muted">Cmimi baze</div>
              <div class="price-val">{{ money(drawer.default_price) }}</div>
            </div>
            <div>
              <div class="muted">Delta</div>
              <div>
                <span v-if="drawer.delta === 'up'" class="chip up">Rritje</span>
                <span v-else-if="drawer.delta === 'down'" class="chip down">Ulje</span>
                <span v-else class="chip muted">—</span>
              </div>
            </div>
          </div>

          <div v-if="drawer.is_offer || drawer.source" class="drawer-block">
            <div class="section-title" style="margin-bottom: 6px">Burimi</div>
            <p>
              <span class="chip" :class="{ offer: drawer.is_offer }">{{ sourceLabel(drawer) }}</span>
            </p>
          </div>

          <div class="drawer-block">
            <button type="button" class="button is-small is-primary" @click="openProductKartela">Hap kartelën</button>
          </div>

          <div class="drawer-block" v-if="drawer.events?.length">
            <div class="section-title">Ndryshimet e dites</div>
            <table class="table is-fullwidth is-narrow audit-table">
              <thead>
                <tr>
                  <th>Kur</th>
                  <th>Burimi</th>
                  <th>Kush</th>
                  <th>Detaje</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(ev, idx) in drawer.events" :key="'ev-' + idx">
                  <td>{{ formatLocalDateTime(ev.at) }}</td>
                  <td>
                    <span class="chip muted">{{ ev.source_label || ev.source }}</span>
                    <span v-if="ev.is_offer" class="chip offer">Ofertë</span>
                  </td>
                  <td>{{ ev.actor_name || "—" }}</td>
                  <td>
                    <div v-if="ev.offer_label">{{ ev.offer_label }}</div>
                    <div v-if="ev.batch_id">Batch #{{ ev.batch_id }} {{ ev.filename || "" }}</div>
                    <div v-if="ev.old_price != null">{{ money(ev.old_price) }} → {{ money(ev.price ?? ev.default_price) }}</div>
                    <div v-else class="muted">{{ ev.description || "—" }}</div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="drawer-block" v-if="drawer.audits?.length">
            <div class="section-title">Audite (±7 dite)</div>
            <table class="table is-fullwidth is-narrow audit-table">
              <thead>
                <tr>
                  <th>Kur</th>
                  <th>Veprimi</th>
                  <th>Burimi</th>
                  <th>Kush</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="a in drawer.audits" :key="'a-' + a.id">
                  <td>{{ formatLocalDateTime(a.at) }}</td>
                  <td>{{ a.action_label || a.action }}</td>
                  <td>{{ a.source_label || a.source || "—" }}</td>
                  <td>{{ a.actor_name || "—" }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </aside>
    </div>

    <div v-if="formEdit" class="calendar-kartela-backdrop" @click.self="closeProductKartela">
      <div class="calendar-kartela-drawer">
        <div class="calendar-kartela-head">
          <div>
            <div class="calendar-kartela-title">Kartela e produktit</div>
            <div class="calendar-kartela-sub">{{ drawer.product?.code }} — {{ drawer.product?.name }}</div>
          </div>
          <button type="button" class="button is-small is-light" @click="closeProductKartela">
            <i class="fa fa-times"></i> Mbyll
          </button>
        </div>
        <div class="calendar-kartela-body">
          <div v-if="formEdit.loading" class="empty-note">
            <i class="fa fa-circle-notch fa-spin"></i> Duke hapur kartelën...
          </div>
          <DaForm
            v-else
            :form="formEdit"
            :closes_form="true"
            :saving="productSaving"
            @submitted="submitProductKartela"
            @close="closeProductKartela"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Api from "@/services/Api";
import DaForm from "@/components/daform/index.vue";
import editProduct from "@/pages/products/forms/product form/edit";
import ProductsServices from "@/services/Product";
import { productFinancaSaveNotice } from "@/utils/financaSaveNotice";
import permissionMixin from "@/mixins/permissions-mixin.js";
import "../shared/pageStyles.css";

const MONTH_LABELS = [
  "Janar",
  "Shkurt",
  "Mars",
  "Prill",
  "Maj",
  "Qershor",
  "Korrik",
  "Gusht",
  "Shtator",
  "Tetor",
  "Nëntor",
  "Dhjetor",
];

const RANGE_MAX_DAYS = 93;

function toYmd(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function daysInclusive(fromStr, toStr) {
  const from = new Date(`${fromStr}T00:00:00`);
  const to = new Date(`${toStr}T00:00:00`);
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return 0;
  return Math.round((to - from) / 86400000) + 1;
}

function monthBounds(year, month) {
  const from = new Date(year, month - 1, 1);
  const to = new Date(year, month, 0);
  return { from: toYmd(from), to: toYmd(to) };
}

export default {
  name: "PriceCalendar",
  mixins: [permissionMixin],
  components: { DaForm },
  data() {
    const now = new Date();
    const bounds = monthBounds(now.getFullYear(), now.getMonth() + 1);
    return {
      periodMode: "month",
      year: now.getFullYear(),
      month: now.getMonth() + 1,
      dateFrom: bounds.from,
      dateTo: bounds.to,
      targetField: "price",
      page: 1,
      perPage: 100,
      total: 0,
      totalPages: 1,
      days: [],
      rows: [],
      loading: false,
      loadingMore: false,
      loadToken: 0,
      exporting: false,
      errorMessage: "",
      reloadTimer: null,
      formEdit: null,
      productSaving: false,
      changeFilters: {
        up: false,
        down: false,
        offer: false,
      },
      filters: {
        tipi: "",
        category_level_1: "",
        category_level_2: "",
        category_level_3: "",
        code_name: "",
        description: "",
      },
      categoryOptions: { level1: [], level2: [], level3: [] },
      drawer: {
        open: false,
        loading: false,
        error: "",
        product: null,
        date: "",
        price: null,
        default_price: null,
        delta: null,
        source: null,
        is_offer: false,
        events: [],
        audits: [],
      },
    };
  },
  computed: {
    monthOptions() {
      return MONTH_LABELS.map((label, idx) => ({ value: idx + 1, label }));
    },
    yearOptions() {
      const y = new Date().getFullYear();
      const list = [];
      for (let i = y - 3; i <= y + 1; i++) list.push(i);
      return list;
    },
    anyChangeFilterActive() {
      return !!(this.changeFilters.up || this.changeFilters.down || this.changeFilters.offer);
    },
    activeChangeFilters() {
      const out = [];
      if (this.changeFilters.up) out.push("up");
      if (this.changeFilters.down) out.push("down");
      if (this.changeFilters.offer) out.push("offer");
      return out;
    },
    selectedDayCount() {
      if (this.periodMode === "custom") {
        if (!this.dateFrom || !this.dateTo) return 0;
        return daysInclusive(this.dateFrom, this.dateTo);
      }
      const bounds = monthBounds(this.year, this.month);
      return daysInclusive(bounds.from, bounds.to);
    },
  },
  methods: {
    setPeriodMode(mode) {
      if (mode === this.periodMode) return;
      if (mode === "custom") {
        const bounds = monthBounds(this.year, this.month);
        this.dateFrom = bounds.from;
        this.dateTo = bounds.to;
      }
      this.periodMode = mode;
      this.page = 1;
      this.loadMatrix();
    },
    applyPreset(kind) {
      const today = new Date();
      if (kind === "month") {
        const bounds = monthBounds(today.getFullYear(), today.getMonth() + 1);
        this.dateFrom = bounds.from;
        this.dateTo = bounds.to;
        this.year = today.getFullYear();
        this.month = today.getMonth() + 1;
      } else {
        const days = kind === "7" ? 6 : 29;
        const from = new Date(today);
        from.setDate(today.getDate() - days);
        this.dateFrom = toYmd(from);
        this.dateTo = toYmd(today);
      }
      this.page = 1;
      this.loadMatrix();
    },
    validatePeriod() {
      if (this.periodMode !== "custom") return null;
      if (!this.dateFrom || !this.dateTo) {
        return 'Plotëso datat "Nga" dhe "Deri".';
      }
      const count = daysInclusive(this.dateFrom, this.dateTo);
      if (count <= 0) {
        return 'Data "Nga" nuk mund të jetë pas datës "Deri".';
      }
      if (count > RANGE_MAX_DAYS) {
        return `Periudha është shumë e gjatë (${count} ditë). Maksimumi është ${RANGE_MAX_DAYS} ditë.`;
      }
      return null;
    },
    periodPayload() {
      if (this.periodMode === "custom") {
        return { date_from: this.dateFrom, date_to: this.dateTo };
      }
      return { year: this.year, month: this.month };
    },
    toggleChangeFilter(key) {
      this.changeFilters[key] = !this.changeFilters[key];
      this.page = 1;
      this.loadMatrix();
    },
    clearChangeFilters() {
      this.changeFilters = { up: false, down: false, offer: false };
      this.page = 1;
      this.loadMatrix();
    },
    scheduleReload() {
      if (this.reloadTimer) clearTimeout(this.reloadTimer);
      this.reloadTimer = setTimeout(() => {
        this.page = 1;
        this.loadMatrix();
      }, 350);
    },
    onPeriodChange() {
      this.page = 1;
      this.loadMatrix();
    },
    refreshMatrix() {
      this.loadMatrix();
    },
    setTargetField(field) {
      if (this.targetField === field) return;
      this.targetField = field;
      this.page = 1;
      this.loadMatrix();
    },
    goPage(p) {
      this.page = p;
      this.loadMatrix();
    },
    clearHover() {
      this.setHoverCol(null);
    },
    onMatrixMouseOver(e) {
      const el = e.target.closest("td.day-cell, th.day-head");
      if (!el || !this.$refs.matrixTable || !this.$refs.matrixTable.contains(el)) {
        return;
      }
      const col = el.getAttribute("data-col");
      if (!col) return;
      this.setHoverCol(col);
    },
    setHoverCol(col) {
      if (col === this._hoverCol) return;
      if (this._hoverColEls && this._hoverColEls.length) {
        for (let i = 0; i < this._hoverColEls.length; i++) {
          this._hoverColEls[i].classList.remove("col-hover");
        }
      }
      this._hoverCol = col;
      this._hoverColEls = null;
      if (!col || !this.$refs.matrixTable) return;
      const escaped =
        typeof CSS !== "undefined" && CSS.escape ? CSS.escape(col) : String(col).replace(/"/g, '\\"');
      this._hoverColEls = this.$refs.matrixTable.querySelectorAll(`[data-col="${escaped}"]`);
      for (let i = 0; i < this._hoverColEls.length; i++) {
        this._hoverColEls[i].classList.add("col-hover");
      }
    },
    async loadCategoryOptions() {
      try {
        const res = await Api(true).post("product/pricing-offers/calendar/category-options", {
          level1: this.filters.category_level_1 ? [this.filters.category_level_1] : [],
          level2: this.filters.category_level_2 ? [this.filters.category_level_2] : [],
        });
        this.categoryOptions = {
          level1: res?.data?.level1 || [],
          level2: res?.data?.level2 || [],
          level3: res?.data?.level3 || [],
        };
      } catch (_) {
        /* keep previous */
      }
    },
    async onCategoryL1Change() {
      this.filters.category_level_2 = "";
      this.filters.category_level_3 = "";
      await this.loadCategoryOptions();
      this.scheduleReload();
    },
    async onCategoryL2Change() {
      this.filters.category_level_3 = "";
      await this.loadCategoryOptions();
      this.scheduleReload();
    },
    money(value) {
      if (value == null || value === "") return "—";
      const n = Number(value);
      if (Number.isNaN(n)) return "—";
      return n.toLocaleString("sq-AL", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
    },
    formatCell(value) {
      if (value == null || value === "") return "—";
      const n = Number(value);
      if (Number.isNaN(n)) return "—";
      return n.toLocaleString("sq-AL", { maximumFractionDigits: 0 });
    },
    formatLocalDateTime(value) {
      if (!value) return "—";
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return String(value);
      return d.toLocaleString("sq-AL", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    sourceLabel(drawer) {
      if (drawer.is_offer) return "Ofertë";
      if (!drawer.source) return "—";
      const map = {
        offer_engine: "Ofertë",
        offer_campaign: "Ofertë",
        price_batch: "Batch",
        price_schedule: "Planifikuar",
        product_form: "Edit produkti",
        product_grid: "Grid produkti",
      };
      return map[drawer.source] || drawer.source;
    },
    filterPayload() {
      const f = {
        tipi: this.filters.tipi || undefined,
        code_name: this.filters.code_name || undefined,
        description: this.filters.description || undefined,
      };
      if (this.filters.category_level_1) f.category_level_1 = [this.filters.category_level_1];
      if (this.filters.category_level_2) f.category_level_2 = [this.filters.category_level_2];
      if (this.filters.category_level_3) f.category_level_3 = [this.filters.category_level_3];
      if (this.activeChangeFilters.length) f.change_filters = this.activeChangeFilters;
      return f;
    },
    matrixRequestBody(serverPage, chunkSize) {
      return {
        ...this.periodPayload(),
        page: serverPage,
        per_page: chunkSize,
        target_field: this.targetField,
        ...this.filterPayload(),
      };
    },
    async loadMatrix() {
      const periodError = this.validatePeriod();
      if (periodError) {
        this.errorMessage = periodError;
        this.rows = [];
        this.days = [];
        this.loading = false;
        this.loadingMore = false;
        return;
      }

      const token = ++this.loadToken;
      const target = this.perPage;
      const dayCount = this.selectedDayCount;
      let chunk = 25;
      if (this.anyChangeFilterActive) {
        chunk = target;
      } else if (dayCount > 45) {
        chunk = 15;
      }
      const logicalStart = (this.page - 1) * target;

      this.loading = true;
      this.loadingMore = false;
      this.errorMessage = "";
      this.rows = [];
      this.clearHover();

      try {
        let collected = [];
        let offset = logicalStart;
        let first = true;

        while (collected.length < target) {
          if (token !== this.loadToken) return;
          const serverPage = Math.floor(offset / chunk) + 1;
          const res = await Api(true).post(
            "product/pricing-offers/calendar/matrix",
            this.matrixRequestBody(serverPage, chunk)
          );
          if (token !== this.loadToken) return;

          const data = res?.data || {};
          if (first) {
            this.days = Array.isArray(data.days) ? data.days : [];
            this.total = data.total || 0;
            this.totalPages = Math.max(1, Math.ceil(this.total / target));
            first = false;
            this.loading = false;
          }

          const batch = Array.isArray(data.rows) ? data.rows : [];
          collected = collected.concat(batch);
          this.rows = collected.slice(0, target);

          if (batch.length < chunk || collected.length >= this.total - logicalStart) {
            break;
          }
          offset += chunk;
          if (offset >= logicalStart + target) {
            break;
          }
          this.loadingMore = true;
        }
      } catch (e) {
        if (token !== this.loadToken) return;
        this.errorMessage = e?.response?.data?.message || "Nuk u ngarkua kalendari.";
        this.rows = [];
        this.days = [];
      } finally {
        if (token === this.loadToken) {
          this.loading = false;
          this.loadingMore = false;
        }
      }
    },
    async openCell(row, cell) {
      const date =
        cell.key ||
        `${this.year}-${String(this.month).padStart(2, "0")}-${String(cell.d).padStart(2, "0")}`;
      this.drawer = {
        open: true,
        loading: true,
        error: "",
        product: { id: row.id, guid: row.guid, code: row.code, name: row.name },
        date,
        price: null,
        default_price: null,
        delta: cell.delta,
        source: cell.source,
        is_offer: !!cell.is_offer,
        events: [],
        audits: [],
      };
      try {
        const res = await Api(true).post("product/pricing-offers/calendar/cell", {
          product_id: row.id,
          date,
        });
        const data = res?.data || {};
        this.drawer.loading = false;
        this.drawer.product = data.product || this.drawer.product;
        this.drawer.price = data.price;
        this.drawer.default_price = data.default_price;
        this.drawer.delta = data.delta;
        this.drawer.source = data.source;
        this.drawer.is_offer = !!data.is_offer;
        this.drawer.events = data.events || [];
        this.drawer.audits = data.audits || [];
      } catch (e) {
        this.drawer.loading = false;
        this.drawer.error = e?.response?.data?.message || "Nuk u ngarkua detaji.";
      }
    },
    closeDrawer() {
      this.drawer.open = false;
    },
    async openProductKartela() {
      const guid = this.drawer.product?.guid;
      if (!guid) {
        this.$toast?.error?.("Mungon GUID i produktit.");
        return;
      }
      if (!this.am_allowed("product.can_edit_product") && !this.am_allowed("product.can_see_product_details")) {
        this.$toast?.error?.("Nuk keni te drejta te hapni kartelen e produktit.");
        return;
      }
      this.formEdit = { loading: true };
      try {
        this.formEdit = await editProduct({ guid });
      } catch (e) {
        this.formEdit = null;
        this.$toast?.error?.(e?.response?.data?.message || e?.message || "Nuk u hap kartela.");
      }
    },
    closeProductKartela() {
      this.formEdit = null;
      this.productSaving = false;
    },
    async submitProductKartela(payload, closeOnSubmit = true, onAfterCreate = null, onDone = null) {
      void onAfterCreate;
      if (this.productSaving) return;
      if (!this.am_allowed("product.can_edit_product")) {
        this.$toast?.error?.("Nuk keni te drejte te modifikoni produktin.");
        if (typeof onDone === "function") onDone();
        return;
      }
      this.productSaving = true;
      try {
        const guid = this.formEdit?.guid || payload?.guid || this.drawer?.product?.guid;
        if (!guid) throw new Error("Nuk u gjet GUID i produktit.");
        const product = await ProductsServices.editProduct({ ...payload, guid });
        const notice = productFinancaSaveNotice("edit", product);
        this.$toast?.[notice.level]?.(notice.text);
        if (closeOnSubmit) this.closeProductKartela();
      } catch (e) {
        this.$toast?.error?.(e?.response?.data?.message || e?.message || "Ruajtja deshtoi.");
      } finally {
        this.productSaving = false;
        if (typeof onDone === "function") onDone();
      }
    },
    async exportCsv() {
      const periodError = this.validatePeriod();
      if (periodError) {
        this.errorMessage = periodError;
        this.$toast?.error?.(periodError);
        return;
      }
      this.exporting = true;
      this.errorMessage = "";
      try {
        const res = await Api(true).post(
          "product/pricing-offers/calendar/export",
          {
            ...this.periodPayload(),
            target_field: this.targetField,
            ...this.filterPayload(),
          },
          { responseType: "blob" }
        );
        const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        const period = this.periodPayload();
        a.download = period.date_from
          ? `kalendari-cmimeve-${period.date_from}-${period.date_to}.csv`
          : `kalendari-cmimeve-${this.year}-${String(this.month).padStart(2, "0")}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } catch (e) {
        let message = "Eksporti deshtoi.";
        const data = e?.response?.data;
        if (data instanceof Blob) {
          try {
            const text = await data.text();
            const parsed = JSON.parse(text);
            message = parsed.message || message;
          } catch (_) {
            /* ignore */
          }
        } else if (data?.message) {
          message = data.message;
        }
        this.errorMessage = message;
        this.$toast?.error?.(message);
      } finally {
        this.exporting = false;
      }
    },
  },
  async created() {
    const allowed =
      this.am_allowed("pricing.can_see_calendar") || this.am_allowed("menu_roles.can_see_catalog_pricing");
    if (!allowed) {
      window.location.href = "/home";
      return;
    }
    if (this.$root?.setCurrentRoute) {
      this.$root.setCurrentRoute("cmimet-kalendar");
    }
    await this.loadCategoryOptions();
    await this.loadMatrix();
  },
  beforeUnmount() {
    if (this.reloadTimer) clearTimeout(this.reloadTimer);
  },
};
</script>

<style scoped>
.calendar-hero-controls {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  justify-content: flex-end;
}
.period-mode-switch {
  flex-shrink: 0;
}
.period-presets {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  align-items: center;
}
.calendar-field {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #4b5563;
}
.calendar-field .input-clean {
  min-width: 110px;
}
.calendar-search {
  min-width: 180px;
}
.calendar-price-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
}
.calendar-filter-row {
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #eef2f7;
}
.calendar-filter-row .input-clean {
  max-width: 200px;
}
.calendar-grid-card {
  padding-bottom: 12px;
}
.calendar-scroll {
  overflow: auto;
  max-height: calc(100vh - 320px);
  border: 1px solid #e5e7eb;
  border-radius: 10px;
}
.calendar-matrix-table {
  border-collapse: separate;
  border-spacing: 0;
  width: max-content;
  min-width: 100%;
  font-size: 12px;
}
.calendar-matrix-table th,
.calendar-matrix-table td {
  border-bottom: 1px solid #eef2f7;
  border-right: 1px solid #f3f4f6;
  padding: 6px 8px;
  white-space: nowrap;
  background: #fff;
}
.calendar-matrix-table thead th {
  position: sticky;
  top: 0;
  z-index: 3;
  background: #f8fafc;
  font-weight: 600;
  color: #374151;
}
.sticky-col {
  position: sticky;
  z-index: 2;
  background: #fff;
}
.calendar-matrix-table thead .sticky-col {
  z-index: 4;
  background: #f8fafc;
}
.col-l1 { left: 0; min-width: 120px; }
.col-l2 { left: 120px; min-width: 120px; }
.col-l3 { left: 240px; min-width: 120px; }
.col-tipi { left: 360px; min-width: 72px; }
.col-code { left: 432px; min-width: 90px; }
.col-name {
  left: 522px;
  min-width: 180px;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  box-shadow: 4px 0 8px -6px rgba(15, 23, 42, 0.25);
}
.day-head {
  text-align: center;
  min-width: 52px;
  position: relative;
}
.day-num { font-weight: 700; }
.day-wd { font-size: 10px; color: #6b7280; text-transform: capitalize; }
.day-arrow {
  position: absolute;
  left: 50%;
  bottom: 0;
  transform: translateX(-50%);
  color: #2563eb;
  font-size: 10px;
  line-height: 1;
  display: none;
}
.day-head.col-hover .day-arrow {
  display: block;
}
.day-cell {
  text-align: right;
  cursor: pointer;
  font-variant-numeric: tabular-nums;
}
.day-cell-inner {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 3px;
}
.delta-mark {
  font-size: 9px;
  line-height: 1;
}
.day-cell:hover {
  outline: 1px solid #93c5fd;
}
.day-cell.delta-up {
  background: #dcfce7 !important;
  color: #166534;
  font-weight: 600;
}
.day-cell.delta-down {
  background: #fee2e2 !important;
  color: #991b1b;
  font-weight: 600;
}
.day-cell.is-offer:not(.delta-up):not(.delta-down) {
  box-shadow: inset 0 0 0 2px #f59e0b;
}
.day-cell.is-offer.delta-up,
.day-cell.is-offer.delta-down {
  box-shadow: inset 0 0 0 2px #f59e0b;
}
/* Row hover via CSS — no Vue re-render */
.calendar-matrix-table tbody tr:hover > td:not(.day-cell.delta-up):not(.day-cell.delta-down) {
  background: #f8fafc;
}
.col-hover:not(.delta-up):not(.delta-down) {
  background: #eff6ff !important;
}
.day-head.col-hover {
  background: #dbeafe !important;
}
.calendar-footer {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-top: 12px;
}
.calendar-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
  font-size: 12px;
  color: #4b5563;
}
.legend-filter {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid #e5e7eb;
  background: #fff;
  border-radius: 999px;
  padding: 6px 12px;
  cursor: pointer;
  font-size: 12px;
  color: #374151;
}
.legend-filter:hover {
  border-color: #93c5fd;
  background: #f8fafc;
}
.legend-filter.active {
  border-color: #2563eb;
  background: #eff6ff;
  color: #1e3a8a;
  font-weight: 600;
  box-shadow: 0 0 0 1px #93c5fd;
}
.legend-filter-hint {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-left: 4px;
  color: #6b7280;
}
.legend-swatch {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 3px;
  vertical-align: -2px;
}
.legend-swatch.up { background: #dcfce7; border: 1px solid #86efac; }
.legend-swatch.down { background: #fee2e2; border: 1px solid #fca5a5; }
.legend-swatch.offer { background: #fff; box-shadow: inset 0 0 0 2px #f59e0b; }
.calendar-pager {
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 8px;
}
.calendar-pager.muted { color: #6b7280; }
.calendar-drawer-backdrop {
  position: fixed;
  top: 3.5rem; /* below navbar (h-14) */
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(15, 23, 42, 0.35);
  z-index: 40;
  display: flex;
  justify-content: flex-end;
}
.calendar-drawer {
  width: min(440px, 100%);
  height: 100%;
  background: #fff;
  box-shadow: -8px 0 24px rgba(15, 23, 42, 0.12);
  display: flex;
  flex-direction: column;
  overflow: auto;
}
.calendar-drawer-head {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 16px;
  border-bottom: 1px solid #e5e7eb;
}
.calendar-drawer-head h2 {
  margin: 0;
  font-size: 18px;
}
.calendar-drawer-head p {
  margin: 2px 0 0;
  color: #4b5563;
}
.calendar-drawer-body {
  padding: 16px;
}
.drawer-prices {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  margin-bottom: 14px;
}
.price-val {
  font-size: 18px;
  font-weight: 700;
}
.drawer-block {
  margin-top: 14px;
}
.muted { color: #6b7280; font-size: 12px; }
.chip.up { background: #dcfce7; color: #166534; }
.chip.down { background: #fee2e2; color: #991b1b; }
.chip.offer { background: #fef3c7; color: #92400e; }

.calendar-kartela-backdrop {
  position: fixed;
  top: 3.5rem;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(15, 23, 42, 0.4);
  z-index: 50;
  display: flex;
  justify-content: flex-end;
}
.calendar-kartela-drawer {
  width: min(720px, 96vw);
  height: 100%;
  background: #fff;
  display: flex;
  flex-direction: column;
  box-shadow: -8px 0 24px rgba(15, 23, 42, 0.15);
}
.calendar-kartela-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px;
  border-bottom: 1px solid #e5e7eb;
}
.calendar-kartela-title {
  font-size: 16px;
  font-weight: 700;
  color: #111827;
}
.calendar-kartela-sub {
  font-size: 12px;
  color: #6b7280;
  margin-top: 2px;
}
.calendar-kartela-body {
  padding: 14px 16px;
  overflow: auto;
  flex: 1;
}
</style>
